import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { locations as staticLocations, type Location } from "./locations-data";

/**
 * Returns the merged list of locations:
 * - Static locations from `locations-data.ts` (with imported image assets + rich detail content)
 * - PLUS any active location in the DB whose slug isn't already in the static list
 *
 * This lets the admin add new points (e.g. Vieux-Boucau) and have them appear
 * on the public site without a code change, while keeping the existing 7 points
 * unchanged (images, descriptions, etc.).
 */
export function useLocations(): Location[] {
  const [merged, setMerged] = useState<Location[]>(staticLocations);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("locations")
      .select("name, slug, address, phone, latitude, longitude, image_url, description, booking_url, type, is_active, sort_order")
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        if (cancelled || !data) return;

        const bySlug = new Map<string, typeof data[number]>();
        for (const row of data) if (row.slug) bySlug.set(row.slug, row);

        // 1. Override existing static locations with DB values when present
        const overridden: Location[] = staticLocations.map((s) => {
          const r = bySlug.get(s.slug);
          if (!r) return s;
          bySlug.delete(s.slug);
          return {
            ...s,
            name: r.name ?? s.name,
            address: r.address ?? s.address,
            phone: r.phone ?? s.phone,
            lat: r.latitude != null ? Number(r.latitude) : s.lat,
            lng: r.longitude != null ? Number(r.longitude) : s.lng,
            image: r.image_url || s.image,
            bookingUrl: r.booking_url || s.bookingUrl,
            description: r.description ?? s.description,
            type: (r.type === "camping" ? "camping" : s.type) as Location["type"],
          };
        });

        // 2. Append new DB-only locations
        const extras: Location[] = Array.from(bySlug.values())
          .filter((r) => r.latitude != null && r.longitude != null)
          .map((r) => ({
            name: r.name ?? "",
            slug: r.slug!,
            address: r.address ?? "",
            city: "",
            postalCode: "",
            lat: Number(r.latitude),
            lng: Number(r.longitude),
            phone: r.phone ?? "",
            image: r.image_url ?? "",
            bookingUrl: r.booking_url ?? "https://ebikeandco.lokki.rent/",
            description: r.description ?? "",
            type: (r.type === "camping" ? "camping" : "village") as Location["type"],
          }));

        setMerged([...overridden, ...extras]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return merged;
}

